import { useSyncExternalStore } from 'react';
import { BookOpen, FileText, Info } from '../../../icons';
import type { AiManualData, ChatMessageEvent } from '../../../types/chat';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

interface AiManualCardProps {
  message: ChatMessageEvent;
}

/**
 * AiManualCard (P2-16h) — 시안 ManualCard line 986~1057 직접 매핑.
 *
 * 구조:
 *   1) Header: BookOpen icon + title + source + "매뉴얼" 뱃지
 *   2) 재직기간별 표 — current row 강조 (primary-tint bg + primary text)
 *   3) 본인 해당 결과 박스 — name + 설명 + result (큰 폰트 primary)
 *   4) 가산 조건 안내 — Info icon + note (highlight + suffix)
 *   5) 출처 (citation) — FileText icon + 한 줄
 *   6) 액션 버튼 2개 (primary + secondary)
 *
 * `message.aiManualData` 가 없으면 fallback placeholder (Phase 5 ai-gateway 미발화 시).
 *
 * primaryAction.url 이 있으면 새 탭 — `target="_blank" rel="noopener noreferrer"`. 외부 링크
 * 보안 기본값.
 */
export function AiManualCard({ message }: AiManualCardProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const data = message.aiManualData;

  if (!data) {
    return <ManualFallback message={message} />;
  }

  return (
    <div className="chat-ai-action-card chat-ai-action-card--manual" role="region" aria-label={data.title}>
      <header className="chat-ai-action-card-header">
        <div className="chat-ai-action-card-icon">
          <BookOpen size={16} />
        </div>
        <div className="chat-ai-action-card-title-block">
          <div className="chat-ai-action-card-title">{data.title}</div>
          <div className="chat-ai-action-card-subtitle">{data.source}</div>
        </div>
        <span className="chat-ai-action-card-badge">{tChat('chat.manual.badge')}</span>
      </header>

      <div className="chat-ai-manual-body">
        {data.rowsLabel && <div className="chat-ai-manual-rows-label">{data.rowsLabel}</div>}

        {data.rows.length > 0 && <ManualRows rows={data.rows} />}

        {(data.user || data.result) && (
          <div className="chat-ai-manual-result-box">
            {data.user && (
              <div className="chat-ai-manual-result-meta">
                {data.user.name}{tChat('chat.manual.user_desc_separator')}{data.user.description}
              </div>
            )}
            {data.result && (
              <div className="chat-ai-manual-result-row">
                <span className="chat-ai-manual-result-label">{data.result.label}</span>
                <span className="chat-ai-manual-result-value">{data.result.value}</span>
              </div>
            )}
          </div>
        )}

        {data.note && <ManualNote note={data.note} />}

        {data.citation && (
          <div className="chat-ai-manual-citation">
            <FileText size={11} aria-hidden />
            <span>{data.citation}</span>
          </div>
        )}

        {(data.primaryAction || data.secondaryAction) && (
          <div className="chat-ai-manual-actions">
            {data.primaryAction && <PrimaryActionButton action={data.primaryAction} />}
            {data.secondaryAction && (
              <button type="button" className="chat-ai-manual-action-button chat-ai-manual-action-button--secondary" disabled>
                {data.secondaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ManualRows({ rows }: { rows: AiManualData['rows'] }) {
  return (
    <div className="chat-ai-manual-rows" role="table" aria-label={tChat('chat.manual.rows_aria')}>
      {rows.map((r, i) => (
        <div
          key={i}
          role="row"
          className={`chat-ai-manual-row${r.current ? ' chat-ai-manual-row--current' : ''}`}
          aria-current={r.current ? 'true' : undefined}
        >
          <span role="cell">
            {r.label}
            {r.current && <span className="chat-ai-manual-row-marker">{tChat('chat.manual.current_marker')}</span>}
          </span>
          <span role="cell" className="chat-ai-manual-row-value">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function ManualNote({ note }: { note: NonNullable<AiManualData['note']> }) {
  return (
    <div className="chat-ai-manual-note">
      <Info size={13} aria-hidden />
      <div className="chat-ai-manual-note-text">
        {note.text}
        {note.highlight && (
          <>
            {' '}
            <span className="chat-ai-manual-note-highlight">{note.highlight}</span>
          </>
        )}
        {note.suffix && <> {note.suffix}</>}
      </div>
    </div>
  );
}

function PrimaryActionButton({ action }: { action: NonNullable<AiManualData['primaryAction']> }) {
  const safeUrl = toSafeHttpUrl(action.url);
  if (safeUrl) {
    return (
      <a
        href={safeUrl}
        className="chat-ai-manual-action-button chat-ai-manual-action-button--primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        {action.label}
      </a>
    );
  }
  return (
    <button type="button" className="chat-ai-manual-action-button chat-ai-manual-action-button--primary" disabled>
      {action.label}
    </button>
  );
}

/**
 * `javascript:` / `data:` / `file:` 등 위험 스킴 차단 — XSS 회피.
 * backend 가 발화한 url 이 신뢰될 수 없을 때(또는 외부 매뉴얼이 인용하는 url) 의 보호.
 */
function toSafeHttpUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return url;
    return null;
  } catch {
    return null;
  }
}

function ManualFallback({ message }: { message: ChatMessageEvent }) {
  return (
    <div className="chat-ai-action-card chat-ai-action-card--manual" role="region" aria-label={tChat('chat.manual.fallback_aria')}>
      <header className="chat-ai-action-card-header">
        <div className="chat-ai-action-card-icon">
          <BookOpen size={16} />
        </div>
        <div className="chat-ai-action-card-title-block">
          <div className="chat-ai-action-card-title">{tChat('chat.manual.fallback_title')}</div>
          <div className="chat-ai-action-card-subtitle">{tChat('chat.manual.fallback_subtitle')}</div>
        </div>
        <span className="chat-ai-action-card-badge">{tChat('chat.manual.badge')}</span>
      </header>
      <div className="chat-ai-action-card-body">
        <div className="chat-ai-action-card-content">{message.content}</div>
      </div>
    </div>
  );
}
